import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { computeNextPosition } from "../dealProfile/profileRules";
import { StatusPill } from "../design/StatusPill";
import type { ProfileOption } from "../types";
import {
  orderProfileOptionsForList,
  toProfileOptionErrorMessage,
  validateProfileOptionLabel,
} from "./profileOptionForm";

/**
 * Almikia Epic EF (TASK-121): the ONE generic catalogue-admin component
 * mounted five times on "Opciones del perfil" (`ProfileOptionsPage.tsx`),
 * one per identical-shape catalogue (DATA CONTRACT EF (1)) -- no
 * per-catalogue copy of list/form code exists anywhere else. Direccion-only
 * writes; RLS/`canAccess` are the real authority (this component assumes its
 * caller already gated the whole screen), so it never checks access itself.
 * "Agregar opción" and "Editar" share one inline label form
 * ({@link OptionLabelForm}); "Archivar"/"Reactivar" share one confirm flow.
 * No delete button anywhere (EF hard constraint).
 * See adr/ADR-ee2f59a9-TASK-121-generic-profile-options-admin.md
 */
export const ProfileOptionList = ({
  resource,
  titleKey,
}: {
  resource: string;
  titleKey: string;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const { data, isPending, error, refetch } = useGetList<ProfileOption>(
    resource,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "position", order: "ASC" },
    },
  );

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<ProfileOption["id"] | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<ProfileOption | null>(
    null,
  );
  const [isArchiving, setIsArchiving] = useState(false);

  const title = translate(titleKey);

  const handleCreate = async (label: string) => {
    // `position` is `integer not null` with no default (01_tables.sql) and
    // the SQL guard never sets it -- mirrors DealActivityTypeCreate.tsx's
    // `nextActivityTypePosition` placement rule, computed over every row of
    // the catalogue (including archived ones) so a new option always lands
    // after the highest existing position.
    await dataProvider.create(resource, {
      data: { label: label.trim(), position: computeNextPosition(data ?? []) },
    });
    setIsAdding(false);
    refetch();
    notify("crm.profileOptions.created_notice", {
      messageArgs: { _: "Opción creada" },
    });
  };

  const handleRename = async (option: ProfileOption, label: string) => {
    await dataProvider.update(resource, {
      id: option.id,
      data: { label: label.trim() },
      previousData: option,
    });
    setEditingId(null);
    refetch();
    notify("crm.profileOptions.updated_notice", {
      messageArgs: { _: "Opción actualizada" },
    });
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    const isArchived = archiveTarget.archived_at != null;
    setIsArchiving(true);
    try {
      await dataProvider.update(resource, {
        id: archiveTarget.id,
        data: { archived_at: isArchived ? null : new Date().toISOString() },
        previousData: archiveTarget,
      });
      notify(
        isArchived
          ? "crm.profileOptions.reactivated_notice"
          : "crm.profileOptions.archived_notice",
        {
          messageArgs: {
            _: isArchived ? "Opción reactivada" : "Opción archivada",
          },
        },
      );
      refetch();
    } catch (writeError) {
      notify(toProfileOptionErrorMessage(writeError), { type: "error" });
    } finally {
      setIsArchiving(false);
      setArchiveTarget(null);
    }
  };

  if (error) {
    return (
      <Card>
        <CardContent className="p-4">
          <h2 className="text-lg font-semibold mb-2">{title}</h2>
          <ListErrorState onRetry={() => refetch()} />
        </CardContent>
      </Card>
    );
  }

  if (isPending || !data) {
    return (
      <Card>
        <CardContent className="p-4">
          <h2 className="text-lg font-semibold mb-2">{title}</h2>
          <ListCardsSkeleton rows={3} />
        </CardContent>
      </Card>
    );
  }

  const ordered = orderProfileOptionsForList(data);
  const isArchivedTarget = archiveTarget?.archived_at != null;

  return (
    <Card data-testid={`profile-option-list-${resource}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          {!isAdding ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="min-h-11"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="size-4 mr-1" />
              {translate("crm.profileOptions.add_action")}
            </Button>
          ) : null}
        </div>

        {isAdding ? (
          <OptionLabelForm
            resource={resource}
            existing={ordered}
            selfId={undefined}
            initialValue=""
            onSave={handleCreate}
            onCancel={() => setIsAdding(false)}
          />
        ) : null}

        {ordered.length === 0 ? (
          <ListEmptyState title={translate("crm.profileOptions.empty")} />
        ) : (
          <div className="divide-y rounded-md border">
            {ordered.map((option) => {
              const isArchived = option.archived_at != null;
              if (editingId === option.id) {
                return (
                  <div key={option.id} className="p-3">
                    <OptionLabelForm
                      resource={resource}
                      existing={ordered}
                      selfId={option.id}
                      initialValue={option.label}
                      onSave={(label) => handleRename(option, label)}
                      onCancel={() => setEditingId(null)}
                    />
                  </div>
                );
              }
              return (
                <div
                  key={option.id}
                  data-testid="profile-option-row"
                  className="flex flex-wrap items-center gap-3 p-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium truncate">
                        {option.label}
                      </span>
                      {isArchived ? (
                        <StatusPill
                          tone="not-ok"
                          text={translate("crm.profileOptions.archived_marker")}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="min-h-11"
                      onClick={() => setEditingId(option.id)}
                    >
                      <Pencil className="size-4 mr-1" />
                      {translate("crm.profileOptions.edit_action")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={isArchived ? "default" : "destructive"}
                      className="min-h-11"
                      onClick={() => setArchiveTarget(option)}
                    >
                      {isArchived
                        ? translate("crm.profileOptions.reactivate_action")
                        : translate("crm.profileOptions.archive_action")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Confirm
        isOpen={archiveTarget != null}
        loading={isArchiving}
        title={
          isArchivedTarget
            ? "crm.profileOptions.confirm_reactivate_title"
            : "crm.profileOptions.confirm_archive_title"
        }
        content={
          isArchivedTarget
            ? "crm.profileOptions.confirm_reactivate_body"
            : "crm.profileOptions.confirm_archive_body"
        }
        confirm={
          isArchivedTarget
            ? "crm.profileOptions.reactivate_action"
            : "crm.profileOptions.archive_action"
        }
        confirmColor={isArchivedTarget ? "primary" : "warning"}
        onConfirm={handleArchiveConfirm}
        onClose={() => setArchiveTarget(null)}
      />
    </Card>
  );
};

/**
 * The single label form shared by "Agregar opción" and "Editar" -- client
 * validation runs `validateProfileOptionLabel` (the same rule the server
 * enforces) before saving, and a server rejection's own Spanish message
 * (`toProfileOptionErrorMessage`) is shown the same way.
 */
const OptionLabelForm = ({
  resource,
  existing,
  selfId,
  initialValue,
  onSave,
  onCancel,
}: {
  resource: string;
  existing: readonly ProfileOption[];
  selfId: ProfileOption["id"] | undefined;
  initialValue: string;
  onSave: (label: string) => Promise<void>;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const [value, setValue] = useState(initialValue);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const inputId = `profile-option-label-${resource}-${selfId ?? "new"}`;

  const handleSave = async () => {
    const message = validateProfileOptionLabel(value, existing, selfId);
    if (message) {
      setValidationError(message);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(value);
    } catch (error) {
      setValidationError(toProfileOptionErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3 bg-muted/30">
      <Label htmlFor={inputId}>
        {translate("crm.profileOptions.label_field")}
      </Label>
      <Input
        id={inputId}
        value={value}
        disabled={isSaving}
        onChange={(event) => {
          setValue(event.target.value);
          setValidationError(undefined);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            handleSave();
          }
        }}
        aria-invalid={validationError != null}
      />
      {validationError ? (
        <p className="text-sm text-destructive">{validationError}</p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="min-h-11"
          disabled={isSaving}
          onClick={onCancel}
        >
          {translate("ra.action.cancel")}
        </Button>
        {/* design-system.md section 6: the single primary action in this
            form opts into the gradient style. */}
        <Button
          type="button"
          size="sm"
          variant="gradient"
          className="min-h-11"
          disabled={isSaving}
          onClick={handleSave}
        >
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};
